import React from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DateCell} from '../../../components/common/table/cells/dateCell';
import {EmptyCell} from '../../../components/common/table/cells/emptyCell';
import {NameCell} from '../../../components/common/table/cells/nameCell';
import {StringTemplateCell} from '../../../components/common/table/cells/stringTemplateCell';
import {TextCell} from '../../../components/common/table/cells/textCell';
import {TableRow, TableRowRenderOptions} from '../../../components/common/table/tableRow';
import {ResourceColumn, ResourceTableColumnTypesEnum} from './resourceTableTypes';

/*
 * Props.
 */

interface ResourceTableRowProps<T extends {id: unknown}> {
  item: T;
  options: TableRowRenderOptions;
  tableColumns: ReadonlyArray<ResourceColumn<T>>;

  isSelected: boolean;
  isInMultiSelectMode: boolean;
  isNextSiblingSelected: boolean;
  isPreviousSiblingSelected: boolean;

  onSelect: Handler;
  onMultiSelect: Handler;
  onCheckBoxToggle: HandlerOf<boolean>;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}

const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const ResourceTableRow = <T extends {id: unknown}>(props: ResourceTableRowProps<T>) => {
  const {
    item,
    options,
    tableColumns,

    isSelected,
    isInMultiSelectMode,
    isPreviousSiblingSelected,
    isNextSiblingSelected,

    onCheckBoxToggle,
    onMultiSelect,
    onSelect,
  } = props;

  const cellRenderer = (
    index: number,
    column: ResourceColumn<T>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    const baseProps = {
      settings: column.settings,
      isHovering,
      isSelected,
      size: options.size,
      index,
      shouldShowStickyGradient,
      shouldRemoveTopBorderRadius: isPreviousSiblingSelected,
      shouldRemoveBottomBorderRadius: isNextSiblingSelected,
    };

    switch (column.type) {
      case ResourceTableColumnTypesEnum.DATE:
        return (
          <DateCell
            key={column.id}
            value={column.accessor(item)}
            customFormat={column.customFormat}
            {...baseProps}
          />
        );

      case ResourceTableColumnTypesEnum.NAME: {
        return (
          <NameCell
            key={column.id}
            value={column.accessor(item)}
            iconName={column.iconName}
            emojiDefinition={column.emojiDefinitionAccessor?.(item)}
            {...baseProps}
          />
        );
      }

      case ResourceTableColumnTypesEnum.STRING_TEMPLATE: {
        return (
          <StringTemplateCell
            key={column.id}
            value={column.accessor(item)}
            expressions={column.expressionsAccessor(item)}
            {...baseProps}
          />
        );
      }

      case ResourceTableColumnTypesEnum.TEXT: {
        return <TextCell key={column.id} value={column.accessor(item)} {...baseProps} />;
      }

      default: {
        return <EmptyCell {...baseProps} />;
      }
    }
  };

  return (
    <StyledTableRow
      $minHeight={options.tableRowHeight}
      onSelect={(event) => {
        if (event.defaultPrevented) {
          return;
        }

        event.preventDefault();

        // If they CMD click, treat the same as checking the checkbox.
        if (event.metaKey) {
          onCheckBoxToggle(!isSelected);
          return;
        }

        // If they shift click, issue a multi select.
        if (event.shiftKey) {
          onMultiSelect();
          return;
        }

        // If we are in multi select mode, treat this as toggling the checkbox.
        if (isInMultiSelectMode) {
          onCheckBoxToggle(!isSelected);
          return;
        }

        // Default to selecting the row.
        onSelect();
      }}
      onCheckBoxClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }

        event.preventDefault();
        onCheckBoxToggle(!isSelected);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      hasHover
      shouldShowRightGradient={options.shouldShowRightGradient}
      isSelected={isSelected}
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};
